import { useEffect, useRef, useState } from 'react';
import { Btn } from '../components/ui/Btn';
import { notificationsApi } from '../features/branding/api';
import { leerTemaLocal } from '../features/branding/tema';
import type { SesionMe } from '../features/session/api';
import { irA, useRuta } from '../lib/ruta';
import { textos } from '../textos';
import { IconoChevron, IconoNavMarca, IconoPanel } from './iconos-nav';
import { guardarEstadoNavEscritorio, leerEstadoNavEscritorio } from './nav-estado';
import { grupoAbierto, grupoDeRuta, NAV_ESCRITORIO, rutaActiva, visibleEnNav } from './nav';
import { SelectorEmpresa } from './SelectorEmpresa';

export function DesktopShell(props: {
  readonly sesion: SesionMe;
  readonly children: React.ReactNode;
  readonly onCerrar: () => void;
  readonly onBuscar: (q: string) => void;
}): React.JSX.Element {
  const ruta = useRuta();
  const [q, setQ] = useState('');
  const [abierta, setAbierta] = useState(false);
  const [avisos, setAvisos] = useState(0);
  const [compacto, setCompacto] = useState(() => leerEstadoNavEscritorio().compacto);
  const [grupos, setGrupos] = useState<Record<string, boolean>>(() => ({ ...leerEstadoNavEscritorio().grupos }));
  const busquedaRef = useRef<HTMLInputElement>(null);
  const pendingG = useRef(false);
  const mostrarMarca = leerTemaLocal()?.showPoweredBy !== false;
  const nombre = leerTemaLocal()?.appName ?? textos.app.nombre;

  useEffect(() => {
    void notificationsApi.noLeidas(props.sesion).then((r) => setAvisos(r.count)).catch(() => undefined);
  }, [props.sesion]);

  useEffect(() => {
    const activo = grupoDeRuta(ruta);
    if (!activo) return;
    setGrupos((prev) => (grupoAbierto(activo, prev) ? prev : { ...prev, [activo]: true }));
  }, [ruta]);

  useEffect(() => {
    guardarEstadoNavEscritorio({ compacto, grupos });
  }, [compacto, grupos]);

  useEffect(() => {
    const enCampo = (ev: KeyboardEvent): boolean =>
      ev.target instanceof HTMLInputElement || ev.target instanceof HTMLTextAreaElement || ev.target instanceof HTMLSelectElement;

    const tecla = (ev: KeyboardEvent): void => {
      if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 'k') {
        ev.preventDefault();
        setAbierta(true);
        return;
      }
      if (ev.key === '/' && !enCampo(ev)) {
        ev.preventDefault();
        setAbierta(true);
        return;
      }
      if (ev.key === 'Escape') {
        setAbierta(false);
        pendingG.current = false;
        return;
      }
      if (enCampo(ev) || ev.ctrlKey || ev.metaKey || ev.altKey) return;
      const k = ev.key.toLowerCase();
      if (pendingG.current) {
        pendingG.current = false;
        if (k === 'a') irA('/activos');
        if (k === 'q') irA('/qr');
        if (k === 'r') irA('/reportes');
        return;
      }
      if (k === 'g') {
        pendingG.current = true;
        window.setTimeout(() => {
          pendingG.current = false;
        }, 800);
        return;
      }
      if (k === 'n') {
        pendingG.current = false;
        const siguiente = (ev2: KeyboardEvent): void => {
          window.removeEventListener('keydown', siguiente, true);
          if (ev2.key.toLowerCase() === 'a' && !enCampo(ev2)) {
            ev2.preventDefault();
            irA('/activos/nuevo');
          }
        };
        window.addEventListener('keydown', siguiente, true);
        window.setTimeout(() => window.removeEventListener('keydown', siguiente, true), 800);
      }
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, []);

  useEffect(() => {
    if (abierta) busquedaRef.current?.focus();
  }, [abierta]);

  return (
    <div className="flex min-h-dvh bg-brand-surface-2" translate="no">
      <aside
        className={`flex shrink-0 flex-col border-r border-brand-border bg-brand-surface transition-[width] ${
          compacto ? 'w-[4.5rem]' : 'w-64'
        }`}
      >
        <div className="flex items-center gap-1 bg-brand-primary px-2 py-3 text-brand-primary-fg">
          {compacto ? (
            <p className="flex-1 text-center text-xl font-semibold">{nombre.slice(0, 1)}</p>
          ) : (
            <p className="flex-1 truncate px-3 text-xl font-semibold tracking-tight">{nombre}</p>
          )}
          <button
            type="button"
            className="flex min-h-tactil min-w-tactil items-center justify-center rounded-brand hover:bg-brand-primary-fg/10"
            aria-label={compacto ? textos.nav.mostrarMenu : textos.nav.ocultarMenu}
            aria-expanded={!compacto}
            onClick={() => setCompacto((v) => !v)}
          >
            <IconoPanel compacto={compacto} />
          </button>
        </div>
        <nav className="flex flex-1 flex-col gap-3 overflow-y-auto px-2 py-3" aria-label={textos.nav.inicio}>
          {NAV_ESCRITORIO.map((grupo) => {
            const visibles = grupo.items.filter((item) => visibleEnNav(props.sesion, item));
            if (visibles.length === 0) return null;
            const abierto = grupoAbierto(grupo.id, grupos);
            const hijos = abierto
              ? visibles.map((item) => {
                  const activo = rutaActiva(ruta, item.href);
                  const etiqueta = item.href === '/avisos' && avisos > 0 ? `${item.etiqueta} (${avisos})` : item.etiqueta;
                  const escanear = item.href === '/escanear';
                  return (
                    <button
                      key={item.href}
                      type="button"
                      title={etiqueta}
                      aria-label={etiqueta}
                      className={
                        escanear
                          ? `flex min-h-tactil items-center rounded-brand bg-brand-secondary font-semibold text-brand-secondary-fg ${
                              compacto ? 'justify-center px-1' : 'ml-3 gap-2 px-3 text-left'
                            }`
                          : `flex min-h-tactil items-center rounded-brand ${
                              compacto ? 'justify-center px-1' : 'ml-3 gap-2 px-3 text-left'
                            } ${
                              activo
                                ? 'bg-brand-primary/10 font-semibold text-brand-primary'
                                : 'text-brand-text hover:bg-brand-surface-2'
                            }`
                      }
                      aria-current={activo ? 'page' : undefined}
                      onClick={() => irA(item.href)}
                    >
                      {item.icono ? <IconoNavMarca nombre={item.icono} /> : null}
                      {compacto ? null : <span className="truncate">{etiqueta}</span>}
                    </button>
                  );
                })
              : null;
            return (
              <div
                key={grupo.id}
                className={
                  compacto
                    ? 'flex flex-col gap-0.5 rounded-brand border border-brand-border bg-brand-surface-2 p-1'
                    : 'flex flex-col gap-0.5'
                }
              >
                <button
                  type="button"
                  className={`flex items-center text-brand-text-muted hover:text-brand-text ${
                    compacto ? 'h-9 justify-center gap-0.5 rounded-brand' : 'justify-between rounded-brand px-2 py-1'
                  }`}
                  aria-expanded={abierto}
                  aria-label={grupo.titulo}
                  title={grupo.titulo}
                  onClick={() => setGrupos((prev) => ({ ...prev, [grupo.id]: !grupoAbierto(grupo.id, prev) }))}
                >
                  {compacto ? <IconoNavMarca nombre={grupo.icono} className="h-4 w-4 shrink-0" /> : null}
                  {compacto ? null : (
                    <span className="text-[11px] font-semibold uppercase tracking-wider">{grupo.titulo}</span>
                  )}
                  <IconoChevron abierto={abierto} />
                </button>
                {hijos ? (
                  compacto ? (
                    <div className="ml-1.5 flex flex-col gap-0.5 border-l border-brand-border pl-0.5">{hijos}</div>
                  ) : (
                    hijos
                  )
                ) : null}
              </div>
            );
          })}
          {mostrarMarca && !compacto ? (
            <p className="mt-auto px-2 text-xs text-brand-text-muted">{textos.pie.poweredBy}</p>
          ) : null}
        </nav>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-4 border-b border-brand-border bg-brand-surface px-6 py-3">
          <p className="max-w-[14rem] truncate text-sm text-brand-text-muted">{props.sesion.user.displayName}</p>
          {props.sesion.platformAdmin ? (
            <button
              type="button"
              className="min-h-tactil shrink-0 rounded-brand px-3 text-sm text-brand-primary"
              onClick={() => irA('/plataforma')}
            >
              {textos.nav.plataforma}
            </button>
          ) : null}
          <SelectorEmpresa sesion={props.sesion} />
          <Btn variante="escanear" onClick={() => irA('/escanear')}>
            {textos.acciones.escanear}
          </Btn>
          <button
            type="button"
            className="min-h-tactil min-w-0 flex-1 rounded-brand border border-brand-border bg-brand-surface-2 px-3 text-left text-sm text-brand-text-muted"
            onClick={() => setAbierta(true)}
          >
            {textos.busqueda.abrir}
          </button>
          <Btn variante="secundario" onClick={props.onCerrar}>
            {textos.acciones.cerrarSesion}
          </Btn>
        </header>
        <main className="flex-1 overflow-auto p-6">{props.children}</main>
      </div>
      {abierta ? (
        <div className="fixed inset-0 z-20 flex items-start justify-center bg-brand-text/40 pt-24">
          <form
            className="w-full max-w-lg rounded-brand border border-brand-border bg-brand-surface p-4 shadow-lg"
            onSubmit={(ev) => {
              ev.preventDefault();
              props.onBuscar(q);
              setAbierta(false);
              irA(`/activos?q=${encodeURIComponent(q)}`);
            }}
          >
            <input
              ref={busquedaRef}
              className="min-h-tactil w-full rounded-brand border border-brand-border px-3 text-brand-text"
              value={q}
              onChange={(ev) => setQ(ev.target.value)}
              placeholder={textos.busqueda.placeholder}
            />
            <Btn variante="primario" className="mt-3" type="submit">
              {textos.busqueda.buscar}
            </Btn>
          </form>
        </div>
      ) : null}
    </div>
  );
}
